import { useState, type FunctionComponent } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { trackElement } from '@fff-web/fff-analytics';
import { GlobalFooter } from '@fff-web/fff-global-footer';
import { useUniversalNavigationContext } from '@fff-web/fff-global-nav';
import { updateNewMemberState, useNMFDDRumView, useUserContext } from '@fff-web/fff-utilities';
import { PersonalSurvey as PersonalSurveyComponent } from '@fff-web/questionnaire-component';
import env from '../../config/env';
import { endTimer } from '../../services/newMemberApi';
import breakpoints from '../../utils/breakpoints';
import useCurrentWidth from '../commons/use-current-width/useCurrentWidth';
import Welcome from '../welcome';
import * as styles from '../App.module.css';

type Props = {
  welcomeText: string;
  afterLastButton: () => void;
  shouldStopTimer: boolean;
  isNewMember: boolean;
};

const SUBSCRIPTION_TYPE_TRIAL = 'TRIAL';
const CUSTOMIZING = 'CUSTOMIZING';

const Survey: FunctionComponent<Props> = ({
  welcomeText,
  afterLastButton,
  shouldStopTimer,
  isNewMember,
}) => {
  const [lastPage] = useQueryParam('lastPage', BooleanParam);
  const { userInfo } = useUserContext();
  const [welcomePage, setWelcomePage] = useState(!lastPage);
  const currentWidth = useCurrentWidth();
  const isDesktop = currentWidth > breakpoints.medium;
  const isTrialUser = userInfo.user.subscriptionType === SUBSCRIPTION_TYPE_TRIAL;

  const { timer, timerLoading } = useUniversalNavigationContext();
  const { flashSaleId } = timer || {};

  useNMFDDRumView({
    init: isNewMember && !isTrialUser && !timerLoading,
    isEditOpen: !!flashSaleId,
    datadogRum,
  });

  const lastButtonAction = async () => {
    if (!isTrialUser && isNewMember) {
      datadogRum.addAction('Finish Personal Survey');
    }

    if (shouldStopTimer) {
      if (!isTrialUser && isNewMember) {
        datadogRum.addAction('End NMF');
      }
      await endTimer();
    } else if (!isTrialUser) {
      await updateNewMemberState(userInfo.user.userId || '', CUSTOMIZING);
    }

    const userId = userInfo.user.userId;
    if (userId) {
      trackElement({
        elementName: 'Submit',
        properties: { option_title: 'Submit' },
      });
    }
    afterLastButton();
  };

  const CurrentPage = welcomePage ? (
    <Welcome onClick={() => setWelcomePage(false)} intro={welcomeText} />
  ) : (
    <PersonalSurveyComponent
      lastButtonAction={lastButtonAction}
      firstBackButtonAction={() => setWelcomePage(true)}
      lastPage={lastPage || false}
    />
  );

  return (
    <>
      {CurrentPage}
      <div
        className={`${styles.footerWrapper} ${
          welcomePage && isDesktop ? styles.footerWrapperWelcome : ''
        }`}
      >
        <GlobalFooter minimalFooter chatbotConfig={{ agentId: env.CHATBOT_AGENT_ID }} />
      </div>
    </>
  );
};

export default Survey;
